import env from "@/env";
import * as amplitude from "@amplitude/analytics-browser";
import { sessionReplayPlugin } from "@amplitude/plugin-session-replay-browser";
import { autocapturePlugin } from "@amplitude/plugin-autocapture-browser";
import { Experiment, Variant } from "@amplitude/experiment-js-client";
import { AnalyticsEventType, CONVERSION_EVENTS } from "@/constants/analytics";
import localCookies from "@/utils/cookie";

// --- Consistent key as defined in strategy https://docs.google.com/document/d/1riR17VnBLKzJmfB7tbsC3PEaLEtV2oIN6HTh4T0_2so/edit?usp=sharing ---
export const ATTRIBUTION_STORAGE_KEY = "initial_attribution_data"; // IMPORTANT: Do not change this key. This is the key for the initial attribution data used in the website.

export const SESSION_UTM_KEY = "session_utm_params"; // IMPORTANT: Do not change this key. This is the key for the session attribution data used in the website.

export const UTM_KEYS = [
  "utm_campaign",
  "utm_source",
  "utm_medium",
  "utm_term",
  "utm_content",
  "gclid",
  "gbraid",
  "wbraid",
  "li_fat_id",
  "fbclid",
  "twclid"
];

let isInitialised = false;

export const initialiseAmplitude = async (options?: amplitude.Types.BrowserOptions) => {
  if (!env.FEATUREFLAG_AMPLITUDE_TRACKING_ENABLED) {
    return;
  }

  if (isInitialised) {
    return amplitude;
  }
  isInitialised = true;

  try {
    if (env.ENVIRONMENT !== "production") {
      console.log("initialised amplitude", options);
    }
    amplitude.init(env.AMPLITUDE_KEY, options);

    const sessionReplayTracking = sessionReplayPlugin();
    amplitude.add(sessionReplayTracking);
    amplitude.add(autocapturePlugin());

    if (env.AMPLITUDE_EXPERIMENT_KEY) {
      await getExperimentClient();
    }

    return amplitude;
  } catch (e) {
    console.log(e);
  }
};

export const getAmplitude = () => {
  return amplitude;
};

/**
 * [STEP 1 & 2] Syncs First-Touch attribution data from localStorage.
 * This should be called ONCE after the user logs in.
 */
export const syncFirstTouchAttribution = () => {
  const storedAttributionString = localCookies.getItem(ATTRIBUTION_STORAGE_KEY);
  if (storedAttributionString) {
    try {
      const storedAttribution = JSON.parse(storedAttributionString);
      const identifyEvent = new amplitude.Identify();

      for (const [key, value] of Object.entries(storedAttribution)) {
        // Correctly use setOnce for First-Touch data
        identifyEvent.setOnce(key, value as string);
      }

      amplitude.identify(identifyEvent);

      // [STEP 3] Cleanup after syncing
      localCookies.removeItem(ATTRIBUTION_STORAGE_KEY);
    } catch (e) {
      console.error("Failed to sync first-touch attribution", e);
    }
  }
};

/**
 * Updates standard user properties that can change over time.
 */
export const updateAmplitudeUserOptions = (userOptions: Record<string, unknown>) => {
  const identifyEvent = new amplitude.Identify();

  for (const [key, value] of Object.entries(userOptions)) {
    // Correctly use .set() for mutable properties like plan, name, etc.
    identifyEvent.set(key, value as string | number | boolean);
  }

  amplitude.identify(identifyEvent);
};

/**
 * Tracks a custom event. For Last-Touch, it reads UTMs from the CURRENT URL.
 */
export const trackAmplitudeEvent = (
  eventName: AnalyticsEventType,
  eventProperties?: Record<string, unknown>
) => {
  try {
    const finalProperties = { ...eventProperties };

    // Logic for Last-Touch attribution ONLY on conversion events
    if (CONVERSION_EVENTS.includes(eventName)) {
      // Logic for Last-Touch attribution on conversion events
      const storedSessionUtms = localCookies.getItem(SESSION_UTM_KEY);
      const sessionParams = storedSessionUtms ? JSON.parse(storedSessionUtms) : {};
      finalProperties["utm_source"] = sessionParams.utm_source || null;
      finalProperties["utm_medium"] = sessionParams.utm_medium || null;
      finalProperties["utm_campaign"] = sessionParams.utm_campaign || null;
      finalProperties["utm_term"] = sessionParams.utm_term || null;
      finalProperties["utm_content"] = sessionParams.utm_content || null;
    }

    amplitude.track(eventName, {
      ...finalProperties,
      environment: env.ENVIRONMENT,
      source: "web"
    });

    if (env.ENVIRONMENT !== "production") {
      console.log(`tracked amplitude event - ${eventName}`, finalProperties);
    }
  } catch (e) {
    if (env.ENVIRONMENT !== "production") {
      console.log("amplitude track event error", e);
    }
  }
};

let experimentClient: ReturnType<typeof Experiment.initializeWithAmplitudeAnalytics> | null = null;
let experimentReady: Promise<
  ReturnType<typeof Experiment.initializeWithAmplitudeAnalytics>
> | null = null;

export const getExperimentClient = async () => {
  if (experimentClient) return experimentClient;

  if (!experimentReady) {
    experimentReady = (async () => {
      const client = Experiment.initializeWithAmplitudeAnalytics(
        env.AMPLITUDE_EXPERIMENT_KEY as string
      );
      await client.fetch(); // fetch once
      experimentClient = client;
      return client;
    })();
  }
  return experimentReady;
};

export const getVariant = async (flagKey: string, fallback = "control") => {
  if (env.ENVIRONMENT === "local" && env.LOCAL_EXPERIMENT_VARIANT) {
    return { value: env.LOCAL_EXPERIMENT_VARIANT } as Variant;
  }
  const client = await getExperimentClient();
  const variant = client.variant(flagKey);
  if (!variant || !variant.value) {
    return { value: fallback } as Variant;
  }
  return variant;
};

/**
 * Returns the variant value without tracking exposure in Amplitude.
 * Useful for logic that needs to happen before the user is "exposed" to the experiment.
 */
export const peekVariant = async (flagKey: string, fallback = "control") => {
  const client = await getExperimentClient();
  // Peek is available in newer versions of the SDK. If not, we access the underlying store.
  // @ts-expect-error - peek might not be in the type definition but exists in the client
  if (typeof client.peek === "function") {
    // @ts-expect-error - client.peek exists at runtime in newer SDK versions
    return client.peek(flagKey) || ({ value: fallback } as Variant);
  }
  // Fallback: use variant() if peek isn't there, or manually check the cache if possible
  return client.variant(flagKey) || ({ value: fallback } as Variant);
};
